import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDollarSign, FiTrendingUp, FiShield, FiCheckCircle, FiArrowRight, FiBriefcase, FiHome, FiUsers } = FiIcons;

const MillionDollarPage = () => {
  const benefits = [
    'Coverage up to $3 million available',
    'Competitive rates for high-value policies',
    'No medical exam for qualifying applicants',
    'Estate planning and wealth transfer benefits',
    'Business succession planning options',
    'Tax-advantaged death benefit for beneficiaries'
  ];

  const idealFor = [
    {
      icon: FiBriefcase,
      title: 'Business Executives',
      description: 'High earners who need substantial coverage to replace significant income and maintain family lifestyle.'
    },
    {
      icon: FiHome,
      title: 'High Net Worth Families',
      description: 'Wealthy families using life insurance for estate planning, tax strategies, and wealth transfer.'
    },
    {
      icon: FiUsers,
      title: 'Business Owners',
      description: 'Entrepreneurs needing coverage for business succession, key person insurance, or buy-sell agreements.'
    }
  ];

  const testimonials = [
    {
      name: 'Michael R.',
      age: 45,
      location: 'Beverly Hills, CA',
      quote: 'As a business owner, I needed substantial coverage for my family and business partners. The $2 million policy gives everyone peace of mind.',
      coverage: '$2,000,000 Term Life'
    },
    {
      name: 'Patricia L.',
      age: 52,
      location: 'Palo Alto, CA',
      quote: 'Working in tech with significant stock options, I needed high coverage amounts. The application process was surprisingly simple for such a large policy.',
      coverage: '$1,500,000 Term Life'
    },
    {
      name: 'David K.',
      age: 38,
      location: 'Newport Beach, CA',
      quote: 'Between our mortgage, kids\' education, and lifestyle, we calculated needing $3 million in coverage. The process made it affordable and easy.',
      coverage: '$3,000,000 Term Life'
    }
  ];

  const pricingData = [
    { coverage: '$1,000,000', male35: '$58', female35: '$46', male45: '$135', female45: '$105' },
    { coverage: '$1,500,000', male35: '$87', female35: '$69', male45: '$203', female45: '$158' },
    { coverage: '$2,000,000', male35: '$116', female35: '$92', male45: '$270', female45: '$210' },
    { coverage: '$2,500,000', male35: '$145', female35: '$115', male45: '$338', female45: '$263' },
    { coverage: '$3,000,000', male35: '$174', female35: '$138', male45: '$405', female45: '$315' }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-purple-800 pt-24 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiDollarSign} className="h-8 w-8 mr-3" />
                <span className="font-semibold">Million Dollar Life Insurance</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                High-Value Protection for High Achievers
              </h1>
              <p className="text-xl text-purple-100 mb-8">
                Substantial life insurance coverage up to $3 million for executives, business owners, and high-income professionals. Competitive rates with simplified underwriting.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  Get My Price
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg p-8 text-secondary-900"
            >
              <h3 className="text-2xl font-bold mb-6">Sample Million Dollar Rates</h3>
              <p className="text-sm text-secondary-600 mb-4">Monthly premiums for 20-year term (healthy, non-smokers)</p>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-sm font-medium text-secondary-700 border-b pb-2">
                  <span>Coverage</span>
                  <span className="text-center">Age 35</span>
                  <span className="text-center">Age 45</span>
                </div>
                {pricingData.map((row, index) => (
                  <div key={index} className="grid grid-cols-3 gap-2 text-sm py-2 border-b border-secondary-100">
                    <span className="font-medium">{row.coverage}</span>
                    <div className="text-center">
                      <div className="text-blue-600">♂ ${row.male35}</div>
                      <div className="text-pink-600">♀ ${row.female35}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-blue-600">♂ ${row.male45}</div>
                      <div className="text-pink-600">♀ ${row.female45}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-secondary-500 mt-4">
                *Sample rates. Your actual rate may vary based on health and other factors.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Why Million Dollar Life Insurance?
              </h2>
              <p className="text-lg text-secondary-600 mb-8">
                High-income earners and business owners often need substantial coverage to protect their family's lifestyle, business interests, and legacy goals.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-secondary-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-secondary-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">Coverage Calculator</h3>
              <p className="text-secondary-600 mb-4">Use the DIME method to estimate your needs:</p>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <span className="font-medium">Debt (mortgage, loans)</span>
                  <span className="text-purple-600">$500,000</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <span className="font-medium">Income (10x annual)</span>
                  <span className="text-purple-600">$1,500,000</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <span className="font-medium">Mortgage balance</span>
                  <span className="text-purple-600">$800,000</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <span className="font-medium">Education (children)</span>
                  <span className="text-purple-600">$200,000</span>
                </div>
                <div className="border-t-2 border-purple-600 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">Total Coverage Needed</span>
                    <span className="font-bold text-lg text-purple-600">$3,000,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            Who Needs Million Dollar Coverage?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {idealFor.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 text-center"
              >
                <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <SafeIcon icon={item.icon} className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">{item.title}</h3>
                <p className="text-secondary-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            What Our High-Value Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-secondary-50 rounded-lg p-6"
              >
                <blockquote className="text-secondary-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-secondary-200 pt-4">
                  <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                  <p className="text-sm text-secondary-600">{testimonial.location}</p>
                  <p className="text-sm text-purple-600 font-medium mt-1">{testimonial.coverage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready for Million Dollar Protection?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Get a personalized quote for high-value life insurance coverage up to $3 million.
          </p>
          <a
            href="https://quote.burbankinsuranceservices.com/Dt2tCn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
          >
            Get My Price
            <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
          </a>
          <p className="mt-4 text-sm text-purple-200">
            Competitive rates • Fast approval • No medical exam for qualifying applicants
          </p>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default MillionDollarPage;